/**
 * @graphql agg
 *
 * AGG (Atlassian GraphQL Gateway) batch user query.
 */

import gql from 'graphql-tag';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { reportError } from '../../utils/reportError.ts';

const BlockServiceAggUserQueryFetchUsersBatchQuery = gql`
	query blockServiceAggUserQueryFetchUsersBatchQuery($accountIds: [ID!]!) {
		users(accountIds: $accountIds) {
			accountId
			name
			picture
		}
	}
`;

type BatchUserInfo = {
	accountId: string;
	displayName: string | null | undefined;
	avatarUrl: string | null | undefined;
};

type AGGUser = {
	accountId: string;
	name: string | null;
	picture: string | null;
};

type FetchUsersBatchData = {
	users?: (AGGUser | null)[] | null;
};

/** Maximum number of account IDs per AGG users batch request. */
const AGG_USERS_BATCH_LIMIT = 90;

function chunk<T>(array: T[], size: number): T[][] {
	const chunks: T[][] = [];
	for (let i = 0; i < array.length; i += size) {
		chunks.push(array.slice(i, i + size));
	}
	return chunks;
}

function mapUsersToBatchUserInfo(users: (AGGUser | null)[] | null | undefined): BatchUserInfo[] {
	if (!users || !Array.isArray(users)) {
		return [];
	}

	return users
		.filter((user): user is AGGUser => user != null && Boolean(user.accountId))
		.map((user) => ({
			accountId: user.accountId,
			displayName: user.name ?? user.accountId,
			avatarUrl: user.picture ?? undefined,
		}));
}

async function fetchUsersBatchSingle(batchAccountIds: string[]): Promise<BatchUserInfo[]> {
	const { data, error } = await queryInterop<FetchUsersBatchData, { accountIds: string[] }>({
		query: BlockServiceAggUserQueryFetchUsersBatchQuery as unknown as Parameters<
			typeof queryInterop
		>[0]['query'],
		variables: { accountIds: batchAccountIds },
		interopApolloAggClient: true,
	});

	error?.graphQLErrors?.forEach((graphQLError) => {
		logFetchError(graphQLError);
		markErrorAsHandled(graphQLError);
	});

	return mapUsersToBatchUserInfo(data?.users);
}

/**
 * Fetches user info for multiple account IDs via AGG batch request(s).
 * When there are more than 90 account IDs, multiple batches are requested in parallel.
 * Returns an array of users returned by the API; missing/deleted users are omitted.
 */
export const fetchUsersBatch = async (accountIds: string[]): Promise<BatchUserInfo[]> => {
	if (accountIds.length === 0) {
		return [];
	}
	try {
		const batches = chunk(accountIds, AGG_USERS_BATCH_LIMIT);
		const results = await Promise.all(batches.map(fetchUsersBatchSingle));
		return results.flat();
	} catch (error) {
		logFetchError(error);
		markErrorAsHandled(error);
		return [];
	}
};

const logFetchError = (maybeError: Error | undefined) => {
	const error = maybeError instanceof Error ? maybeError : new Error(String(maybeError));
	const errorToLog = new Error(
		`Error when fetching Snippet owner user details: ${error?.message || JSON.stringify(error)}`,
	);
	reportError(errorToLog);
};
